import { useEffect, useState } from "react";
import { Checkbox, Form, Modal, Spin, Table } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../../components/buttons/buttons";
import Heading from "../../../components/heading/heading";
import { TableWrapper } from "../../styled";
import { UserTableStyleWrapper } from "../style";
import Notification from "../../../utility/notification";
import HttpStatus from "../../../utility/status.enum";
import { learnFromConversations, bulkAddKnowledgeBase } from "../../../redux/knowledgeBase/actionCreator";

const CONTENT_TYPE_RESOURCES = 1;
const CONTENT_VISIBILITY_ALWAYS = [1];

const tableColumns = [{ title: "Question", dataIndex: "content", key: "content" }];

/**
 * Modal that fetches the last 3 months of PMS conversations, presents AI-extracted
 * Q&A pairs for review, and lets the host select items to add to the Knowledge Base.
 *
 * Uses the same Selected-Listings modal design (bulk-listing-modal / guide-card /
 * user-info checkbox rows) already used elsewhere for reviewing lists in a modal.
 *
 * Props:
 *   visible   {boolean}  — controls modal open/close
 *   onClose   {function} — called when the modal should close
 *   onAdded   {function} — called after items are successfully added (triggers KB refresh)
 */
const LearnFromConversations = ({ visible, onClose, onAdded }) => {
    const dispatch = useDispatch();
    const { learnLoading, learnSuggestions, learnError, bulkAdding } = useSelector(
        (state) => state.KnowledgeBase
    );

    const [selected, setSelected] = useState(new Set());

    // Fetch suggestions whenever the modal opens
    useEffect(() => {
        if (visible) {
            setSelected(new Set());
            dispatch(learnFromConversations());
        }
    }, [visible]);

    // Group suggestions by category (e.g. "Check-In and Check-Out", "Parking") while
    // keeping each item's original index into learnSuggestions for selection state.
    const groupedSuggestions = () => {
        const groups = [];
        const groupIndexByCategory = new Map();
        learnSuggestions.forEach((item, index) => {
            const category = item.category || "General";
            if (!groupIndexByCategory.has(category)) {
                groupIndexByCategory.set(category, groups.length);
                groups.push({ category, items: [] });
            }
            groups[groupIndexByCategory.get(category)].items.push({ ...item, index });
        });
        return groups;
    };

    const isGroupFullySelected = (group) =>
        group.items.length > 0 && group.items.every(({ index }) => selected.has(index));

    const isGroupPartiallySelected = (group) => {
        const selectedCount = group.items.filter(({ index }) => selected.has(index)).length;
        return selectedCount > 0 && selectedCount < group.items.length;
    };

    const toggleGroup = (group) => {
        setSelected((prev) => {
            const next = new Set(prev);
            const shouldSelectAll = !isGroupFullySelected(group);
            group.items.forEach(({ index }) => {
                if (shouldSelectAll) {
                    next.add(index);
                } else {
                    next.delete(index);
                }
            });
            return next;
        });
    };

    const toggleItem = (index) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const handleAddToKB = async () => {
        if (selected.size === 0) {
            Notification("error", "Please select at least one item to add.");
            return;
        }

        const items = [...selected].map((i) => ({
            title: learnSuggestions[i].question,
            description: learnSuggestions[i].answer,
            category: learnSuggestions[i].category || undefined,
            content_type: CONTENT_TYPE_RESOURCES,
            content_visibility: CONTENT_VISIBILITY_ALWAYS,
        }));

        const res = await dispatch(bulkAddKnowledgeBase(items));
        if (res?.status === HttpStatus.HTTP_OK) {
            const saved = res?.result?.saved ?? 0;
            Notification("success", `${saved} item${saved !== 1 ? "s" : ""} added to Knowledge Base.`);
            onAdded?.();
            onClose?.();
        } else {
            Notification("error", res?.message || "Failed to add items. Please try again.");
        }
    };

    // One single Table for the whole modal — using a separate Table per category
    // reliably produces one independent scrollbar per category in this app's Table
    // CSS, no matter how the height/overflow rules are overridden. Category headers
    // are rows in this same table (marked isHeader) so there is exactly one
    // .ant-table-content, and therefore exactly one scrollable region; the boxed
    // row background is stripped back off for header rows via qa-group-header-row
    // in newStyle.css so they still read as plain section labels, not data rows.
    const buildTableData = () => {
        const rows = [];
        groupedSuggestions().forEach((group) => {
            rows.push({
                key: `header-${group.category}`,
                isHeader: true,
                content: (
                    <div className="top-header-title qa-group-header-title">
                        <div className="user-info-checkbox">
                            <Checkbox
                                checked={isGroupFullySelected(group)}
                                indeterminate={isGroupPartiallySelected(group)}
                                onChange={() => toggleGroup(group)}
                            />
                        </div>
                        <h5>
                            <span>{group.category}</span>
                        </h5>
                    </div>
                ),
            });
            group.items.forEach(({ index: i, question, answer }) => {
                rows.push({
                    key: i,
                    content: (
                        <div className="user-info">
                            <div className="user-info-checkbox">
                                <Form.Item className="mb-0" name={`qa_${i}`} labelCol={{ className: "custom-checkbox-label" }}>
                                    <Checkbox checked={selected.has(i)} onChange={() => toggleItem(i)} />
                                </Form.Item>
                            </div>
                            <figcaption>
                                <Heading className="user-name" as="h6">
                                    {question}
                                </Heading>
                                <span className="user-designation" />
                                <span>{answer}</span>
                            </figcaption>
                        </div>
                    ),
                });
            });
        });
        return rows;
    };

    const renderContent = () => {
        if (learnLoading) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 220, gap: 16 }}>
                    <Spin size="large" />
                    <p className="text-center" style={{ color: "#555", marginTop: 12 }}>
                        Retrieving and analysing your last 3 months of conversations&hellip;
                        <br />
                        <small>This may take a moment.</small>
                    </p>
                </div>
            );
        }

        if (learnError) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 140 }}>
                    <p style={{ color: "#ff4d4f" }}>
                        Something went wrong while fetching conversations. Please try again.
                    </p>
                </div>
            );
        }

        if (!learnSuggestions || learnSuggestions.length === 0) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 140 }}>
                    <p style={{ color: "#888", textAlign: "center" }}>
                        No Q&amp;A suggestions were found in your past conversations.
                        <br />
                        Make sure your PMS is connected and has conversation history.
                    </p>
                </div>
            );
        }

        return (
            <div className="guide-matching-listing">
                <div className="project-modal">
                    <div className="guide-card">
                        <div className="guide-card-body">
                            <UserTableStyleWrapper>
                                <div className="contact-table">
                                    <TableWrapper className="table-responsive">
                                        <Table
                                            dataSource={buildTableData()}
                                            columns={tableColumns}
                                            rowKey="key"
                                            pagination={false}
                                            className="listing-no-thead-table"
                                            rowClassName={(record) => (record.isHeader ? "qa-group-header-row" : "")}
                                        />
                                    </TableWrapper>
                                </div>
                            </UserTableStyleWrapper>
                        </div>
                        <div className="bulk-editbtn-box">
                            <Button
                                htmlType="submit"
                                size="default"
                                type="dark"
                                shape="circle"
                                key="submit"
                                onClick={handleAddToKB}
                            >
                                {bulkAdding ? "Adding..." : "Add to Knowledge Base"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            type="primary"
            title={`Learn from Past Conversations (${selected.size}/${learnSuggestions.length})`}
            open={visible}
            footer={null}
            onCancel={onClose}
            width="90%"
            maskClosable={false}
            destroyOnClose
            className="bulk-listing-modal template-config-listing-modal qa-suggestions-modal"
        >
            {renderContent()}
        </Modal>
    );
};

export default LearnFromConversations;
